import { makeAutoObservable, computed, action } from 'mobx';
import { BonusTypeType, BonusTypeZod, CampaignStatusType } from 'src/api_openapi/v2/bonus-engine/schemas';
import { CasinoSectionState } from 'src/domains/casino/webview/bonus-engine/campaign/reward-conditions/casino-section/CasinoSection.state';
import { SportsBookSectionState } from 'src/domains/casino/webview/bonus-engine/campaign/reward-conditions/sportsbook-section/SportsBookSection.state';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { FormModel } from 'src_common/common/mobx-utils/Form2/FormModel';
import { validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';
import { isStringLength } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.validators';
import {
    checkIsArray,
    checkIsNumber,
    formDateToApiDate,
} from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.helpers';
import { deserializeNodeValue } from 'src/domains/casino/webview/bonus-engine/campaign/action-conditions/sportsbook-sections/sports-picker/utils';
import { Common } from 'src/AppState/Common';
import { CampaignModel } from 'src/domains/casino/state/AppCasino/store/campaign-store/CampaignModel';
import {
    FreeSpinUsageConditionsItemType,
    FreeSpinUsageConditionsKeyType,
    FreeSpinUsageConditionsType,
    FreeBetUsageConditionsItemType,
    FreeBetUsageConditionsKeyType,
    FreeBetUsageConditionsType,
    RewardConditionsType,
} from './RewardConditions.types';

interface RewardViewType {
    value: BonusTypeType;
    label: string;
    dataTest?: string;
}

const allRewardsOption: Array<RewardViewType> = [
    { dataTest: 'free-bet', value: 'free-bet', label: 'Free Bet' },
    { dataTest: 'free-spin', value: 'free-spin', label: 'Free Spin' },
];

export type RewardConditionFormType = {
    'bet-type': string;
    'bonus-type': string;
    'reward-name': string;
};

export class RewardConditionsState {
    public readonly betTypeForm: FormInputState<string, string>;
    public readonly bonusTypeForm: FormInputState<string, string>;
    public readonly rewardNameForm: FormInputState<string, string>;
    public readonly casinoSectionState: CasinoSectionState;
    public readonly sportsBookSectionState: SportsBookSectionState;
    private readonly form: FormModel<RewardConditionFormType>;
    public campaignStatus: CampaignStatusType | null = null;

    public constructor(
        public readonly common: Common,
        public readonly campaignId: string | undefined
    ) {
        makeAutoObservable(this);

        this.betTypeForm = FormInputState.new('');
        this.bonusTypeForm = FormInputState.new('').map(
            validateNotEmpty('Select Reward Type to display relevant condition form')
        );
        this.rewardNameForm = FormInputState.new('')
            .map(validateNotEmpty('Enter a maximum of 30 Characters'))
            .map(isStringLength('less', 30, true, false, 'Enter a maximum of 30 Characters'));

        this.casinoSectionState = new CasinoSectionState(this.common, this.campaignId);
        this.sportsBookSectionState = new SportsBookSectionState(this.common, this.campaignId);

        this.form = FormModel.group({
            'bet-type': this.betTypeForm,
            'bonus-type': this.bonusTypeForm,
            'reward-name': this.rewardNameForm,
        });
    }

    @action public validateForm(): boolean {
        this.form.setAsVisited();
        this.sportsBookSectionState.validateForm();
        this.casinoSectionState.validateForm();

        return this.isFormValid;
    }

    public validNotEmpty(): void {
        this.form.mapChild((item) => {
            if (item instanceof FormInputState) {
                const value = item.value;
                if (value.trim() !== '') {
                    item.setAsVisited();
                }
            }

            return item;
        });
    }

    @computed private get isFormValid(): boolean {
        if (this.form.result.value.type === 'error') {
            return false;
        }
        switch (this.bonusTypeForm.value) {
            case 'free-bet':
                return this.sportsBookSectionState.isFormValid;
            case 'free-spin':
                return this.casinoSectionState.isFormValid;
            default:
                return false;
        }
    }

    @computed public get formErrorsCount(): number {
        let errorsCount = this.form.errorsForView.length;
        switch (this.bonusTypeForm.value) {
            case 'free-bet':
                errorsCount += this.sportsBookSectionState.formErrorsCount;
                break;
            case 'free-spin':
                errorsCount += this.casinoSectionState.formErrorsCount;
                break;
        }
        return errorsCount;
    }

    public get allRewardOptions(): Array<RewardViewType> {
        return allRewardsOption;
    }

    public get selectedRewardType(): string {
        return this.bonusTypeForm.value;
    }

    public get rewardType(): BonusTypeType | undefined {
        const value = BonusTypeZod.safeParse(this.bonusTypeForm.value);
        return value.success ? value.data : undefined;
    }

    private get rewardAmount(): number | undefined {
        const amount =
            this.bonusTypeForm.value === 'free-bet'
                ? parseInt(this.sportsBookSectionState.rewardValueForm.value)
                : parseInt(this.casinoSectionState.numberOfFreeSpinsForm.value);
        if (isNaN(amount)) {
            return undefined;
        }
        return this.bonusTypeForm.value === 'free-bet' ? amount * 100 : amount;
    }

    private get expiryAt(): string | undefined {
        const date =
            this.bonusTypeForm.value === 'free-bet'
                ? this.sportsBookSectionState.endDateTimeForm.value
                : this.casinoSectionState.endDateTimeForm.value;

        return formDateToApiDate(date);
    }

    private get relativeExpiryDays(): number | undefined {
        const relativeExpiryDays = this.sportsBookSectionState.relativeExpiryDays.value;
        const relativeExpiryDaysCasino = this.casinoSectionState.relativeExpiryDays.value;
        return this.bonusTypeForm.value === 'free-bet'
            ? isNaN(parseInt(relativeExpiryDays))
                ? undefined
                : parseInt(relativeExpiryDays)
            : isNaN(parseInt(relativeExpiryDaysCasino))
              ? undefined
              : parseInt(relativeExpiryDaysCasino);
    }

    private get installmentCount(): number | undefined {
        const count =
            this.bonusTypeForm.value === 'free-bet'
                ? parseInt(this.sportsBookSectionState.installmentCountForm.value)
                : parseInt(this.casinoSectionState.installmentCountForm.value);
        if (isNaN(count)) {
            return undefined;
        }
        return count;
    }

    private get rewardInterval(): number | undefined {
        const interval =
            this.bonusTypeForm.value === 'free-bet'
                ? parseInt(this.sportsBookSectionState.rewardIntervalForm.value)
                : parseInt(this.casinoSectionState.rewardIntervalForm.value);
        if (isNaN(interval)) {
            return undefined;
        }
        return interval;
    }

    private get scheduledReward(): boolean {
        return this.bonusTypeForm.value === 'free-bet'
            ? this.sportsBookSectionState.scheduledReward
            : this.casinoSectionState.scheduledReward;
    }

    private get rewardDate(): string | undefined {
        if (!this.scheduledReward) return;

        const rewardDate =
            this.bonusTypeForm.value === 'free-bet'
                ? this.sportsBookSectionState.rewardDateForm.value
                : this.casinoSectionState.rewardDateForm.value;

        return formDateToApiDate(rewardDate);
    }

    public get rewardConditions(): RewardConditionsType | undefined {
        switch (this.rewardType) {
            case 'free-spin':
                return [
                    {
                        rewardType: 'free-spin',
                        rewardName: this.rewardNameForm.value,
                        rewardAmount: this.rewardAmount,
                        expiryAt: this.expiryAt,
                        relativeExpiryDays: this.relativeExpiryDays,
                        usageConditions: this.casinoUsageConditions,
                        playthroughEligibleGames: this.casinoSectionState.playThroughEligibleGamesState.checked,
                        playthrough: isNaN(parseFloat(this.casinoSectionState.playThroughForm.value))
                            ? undefined
                            : parseFloat(this.casinoSectionState.playThroughForm.value),
                        totalBet: isNaN(parseFloat(this.casinoSectionState.totalBetForm.value))
                            ? undefined
                            : parseFloat(this.casinoSectionState.totalBetForm.value),
                        installmentCount: this.installmentCount,
                        rewardInterval: this.rewardInterval,
                        scheduledReward: this.scheduledReward,
                        rewardDate: this.rewardDate,
                    },
                ];
            case 'free-bet':
                return [
                    {
                        rewardType: 'free-bet',
                        rewardName: this.rewardNameForm.value,
                        rewardAmount: this.rewardAmount,
                        expiryAt: this.expiryAt,
                        installmentCount: this.installmentCount,
                        rewardInterval: this.rewardInterval,
                        relativeExpiryDays: this.relativeExpiryDays,
                        usageConditions: this.sportsbookUsageConditions,
                        scheduledReward: this.scheduledReward,
                        rewardDate: this.rewardDate,
                    },
                ];
            default:
                return undefined;
        }
    }

    private get casinoUsageConditions(): FreeSpinUsageConditionsType {
        const conditionsMap: Map<FreeSpinUsageConditionsKeyType, string | undefined> = new Map([
            ['casino-game', JSON.stringify(this.casinoSectionState.freeSpinsEligibleGamesState.checked)],
        ]);

        return this.buildCasinoUsageConditions(conditionsMap);
    }

    private get sportsbookUsageConditions(): FreeBetUsageConditionsType {
        const sportsPicker = this.sportsBookSectionState.sportsPickerState.checked.map((e) => deserializeNodeValue(e));

        const sportIds = sportsPicker.filter((e) => e?.type === 'sport').map((e) => e.sportId);
        const competitionIds = sportsPicker.filter((e) => e?.type === 'competition').map((e) => e.competitionId);
        const eventIds = sportsPicker.filter((e) => e?.type === 'event').map((e) => e.eventId);
        const marketIds = sportsPicker.filter((e) => e?.type === 'market').map((e) => e.marketId);

        const conditionsMap: Map<FreeBetUsageConditionsKeyType, string | undefined> = new Map([
            // Betting Types & Odds Criteria
            ['bet-type', this.sportsBookSectionState.betTypeForm.value],
            ['bet-min-odds', this.sportsBookSectionState.minOddsFrom.value],
            // Eligible: Sport > Competition > Event > Market
            ['sport', JSON.stringify(sportIds)],
            ['competition', JSON.stringify(competitionIds)],
            ['event', JSON.stringify(eventIds)],
            ['market', JSON.stringify(marketIds)],
        ]);

        const usageConditions = this.buildSportsbookUsageConditions(conditionsMap);
        return usageConditions;
    }

    @action public setFormValues = async (campaignModel: CampaignModel): Promise<void> => {
        this.campaignStatus = campaignModel.status;

        this.bonusTypeForm.setValue(campaignModel.bonusRewards?.[0]?.rewardType ?? '');
        this.rewardNameForm.setValue(campaignModel.bonusRewards?.[0]?.rewardName ?? '');

        this.casinoSectionState.setFormValues(campaignModel);
        await this.sportsBookSectionState.setFormValues(campaignModel);
    };

    private buildSportsbookUsageConditions = (
        conditionsMap: Map<FreeBetUsageConditionsKeyType, string | undefined>
    ): FreeBetUsageConditionsType => {
        const criteriaRecord: Record<
            FreeBetUsageConditionsKeyType,
            (value: string | undefined) => FreeBetUsageConditionsItemType | null
        > = {
            'bet-min-odds': (value) => {
                const parsedValue = checkIsNumber(value);
                if (parsedValue === null) {
                    return null;
                }
                return { type: 'bet-min-odds', value: parsedValue, op: 'gte' };
            },
            'bet-type': (value) => {
                const parsedValue = checkIsArray(value);
                if (parsedValue === null) {
                    return null;
                }
                return { type: 'bet-type', value: parsedValue, op: 'in' };
            },
            competition: (value) => {
                const parsedValue = checkIsArray(value);
                if (parsedValue === null || parsedValue.length === 0) {
                    return null;
                }
                return { type: 'competition', value: parsedValue, op: 'in' };
            },
            sport: (value) => {
                const parsedValue = checkIsArray(value);
                if (parsedValue === null || parsedValue.length === 0) {
                    return null;
                }
                return { type: 'sport', value: parsedValue, op: 'in' };
            },
            event: (value) => {
                const parsedValue = checkIsArray(value);
                if (parsedValue === null || parsedValue.length === 0) {
                    return null;
                }
                return { type: 'event', value: parsedValue, op: 'in' };
            },
            market: (value) => {
                const parsedValue = checkIsArray(value);
                if (parsedValue === null || parsedValue.length === 0) {
                    return null;
                }
                return { type: 'market', value: parsedValue, op: 'in' };
            },
        };

        const criteria: Array<FreeBetUsageConditionsItemType> = [];

        for (const [key, value] of conditionsMap) {
            const getCriteriaItem = criteriaRecord[key];
            const criteriaItem = getCriteriaItem(value);

            if (criteriaItem !== null) {
                criteria.push(criteriaItem);
            }
        }

        return criteria;
    };

    private buildCasinoUsageConditions = (
        conditionsMap: Map<FreeSpinUsageConditionsKeyType, string | undefined>
    ): FreeSpinUsageConditionsType => {
        const criteriaRecord: Record<
            FreeSpinUsageConditionsKeyType,
            (value: string | undefined) => FreeSpinUsageConditionsItemType | undefined
        > = {
            'casino-game': (value) => {
                const parsedValue = checkIsArray(value);
                if (parsedValue === null || parsedValue.length === 0) {
                    return undefined;
                }
                return { type: 'casino-game', value: parsedValue, op: 'in' };
            },
        };

        const criteria: Array<FreeSpinUsageConditionsItemType> = [];

        for (const [key, value] of conditionsMap) {
            const getCriteriaItem = criteriaRecord[key];
            const criteriaItem = getCriteriaItem(value);

            if (criteriaItem !== undefined) {
                criteria.push(criteriaItem);
            }
        }

        return criteria;
    };

    public isTouched(): boolean {
        return this.form.isTouched() || this.sportsBookSectionState.isTouched() || this.casinoSectionState.isTouched();
    }

    public setIsTouched(): void {
        this.form.setIsTouched();

        this.sportsBookSectionState.setIsTouched();
        this.casinoSectionState.setIsTouched();
    }

    public resetIsTouched(): void {
        this.form.resetIsTouched();

        this.sportsBookSectionState.resetIsTouched();
        this.casinoSectionState.resetIsTouched();
    }

    public resetIsVisited(): void {
        this.form.resetIsVisited();

        this.sportsBookSectionState.resetIsVisited();
        this.casinoSectionState.resetIsVisited();
    }
}
